'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { createBatchWaitTracker, type BatchWaitContext, type BatchWaitEndReason } from '@/lib/review-batch-wait';

export function useReviewBatchWaitAnalytics(state: {
  context: BatchWaitContext | null;
  showing: boolean;
  obscured: boolean;
  endReason: BatchWaitEndReason;
}) {
  const posthog = usePostHog();
  const client = useRef(posthog);
  const latest = useRef(state);
  const pageHidden = useRef(false);
  const lifecycle = useRef({ mounted: false });
  const tracker = useRef<ReturnType<typeof createBatchWaitTracker> | null>(null);
  const sync = useCallback(() => {
    const current = latest.current;
    tracker.current?.update(current.context, current.showing && !pageHidden.current && Boolean(client.current),
      document.visibilityState === 'visible' && !current.obscured, current.endReason);
  }, []);

  useEffect(() => {
    if (!tracker.current) tracker.current = createBatchWaitTracker({
      now: () => performance.now(),
      createId: () => crypto.randomUUID(),
      capture: (event, properties, leavingPage) => client.current?.capture(event, properties,
        leavingPage ? { transport: 'sendBeacon', send_instantly: true } : undefined),
    });
    const wait = tracker.current;
    const scope = lifecycle.current;
    scope.mounted = true;
    const onVisibility = () => sync();
    const onPageHide = () => {
      pageHidden.current = true;
      wait.finish('page_left', true);
    };
    const onPageShow = () => { pageHidden.current = false; sync(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      scope.mounted = false;
      // Strict Mode replays effects synchronously. Only a real unmount ends a wait.
      queueMicrotask(() => { if (!scope.mounted) wait.finish('left_review'); });
    };
    // The tracker is stable; callbacks read the latest committed state through refs.
  }, [sync]);

  useEffect(() => {
    client.current = posthog;
    latest.current = state;
    sync();
  }, [posthog, state, sync]);
}
